import { SettingsState } from '../stores/useSettingsStore';
import { encryptionService, SecretData, EncryptedData } from '../utils/encryption';

type SecureSettings = Pick<SettingsState, 'security' | 'billingConfig'>;
type LocalSettings = Pick<SettingsState, 'theme' | 'notifications' | 'workspacePrefs'>;

export interface SettingsServiceConfig {
  apiBaseUrl?: string;
  timeout?: number;
}

export interface SecretMetadata {
  id: string;
  name: string;
  environment: 'development' | 'staging' | 'production';
  lastUsed: string;
  createdAt: string;
  updatedAt: string;
}

export interface SecretCreateRequest {
  name: string;
  value: string;
  environment: 'development' | 'staging' | 'production';
}

export interface SecretUpdateRequest {
  name?: string;
  value?: string;
  environment?: 'development' | 'staging' | 'production';
}

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  bio: string;
  avatar: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileUpdateRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  location?: string;
  bio?: string;
  avatar?: string | null;
}

export interface BillingSubscription {
  id: string;
  status: 'active' | 'inactive' | 'past_due' | 'canceled' | 'trialing';
  plan: {
    id: string;
    name: string;
    price: number;
    interval: 'month' | 'year';
    features: string[];
  };
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  usage: {
    apiCalls: number;
    storage: number;
    bandwidth: number;
  };
  limits: {
    apiCalls: number;
    storage: number;
    bandwidth: number;
  };
}

export interface PaymentMethod {
  id: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
  isDefault: boolean;
}

export interface SubscriptionUpdateRequest {
  planId?: string;
  cancelAtPeriodEnd?: boolean;
}

export interface Integration {
  id: string;
  name: string;
  description: string;
  connected: boolean;
  status: 'active' | 'inactive' | 'error';
  lastSync: string;
  authUrl?: string;
  webhookUrl?: string;
}

export interface PropolisConfig {
  host: string;
  port: number;
  authToken?: string;
  webhookUrl?: string;
  enabled: boolean;
}

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: string;
  lastTriggered?: string;
}

export interface WebhookCreateRequest {
  name: string;
  url: string;
  events: string[];
}

export interface WebhookUpdateRequest {
  name?: string;
  url?: string;
  events?: string[];
  active?: boolean;
}

export interface ReferralStats {
  totalReferrals: number;
  successfulReferrals: number;
  pendingReferrals: number;
  totalEarnings: number;
  thisMonthEarnings: number;
}

export interface ReferralHistory {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'pending' | 'expired';
  earnings: number;
  date: string;
  referralCode: string;
}

export interface ReferralData {
  referralCode: string;
  stats: ReferralStats;
  history: ReferralHistory[];
}

export interface ReferralInvite {
  email: string;
  message?: string;
}

export interface Deployment {
  id: string;
  name: string;
  environment: 'production' | 'staging' | 'development';
  status: 'active' | 'deploying' | 'inactive' | 'failed';
  url: string;
  branch: string;
  lastDeploy: string;
  version: string;
  health: 'healthy' | 'deploying' | 'stopped' | 'error';
  provider: 'vercel' | 'netlify' | 'aws';
  providerId: string;
}

export interface DeploymentHistory {
  id: string;
  environment: string;
  version: string;
  status: 'success' | 'deploying' | 'failed';
  duration: string;
  timestamp: string;
  deploymentId: string;
}

export interface DeploymentSettings {
  autoDeployOnMerge: boolean;
  healthChecks: boolean;
  slackNotifications: boolean;
  rollbackOnFailure: boolean;
}

export interface DeploymentData {
  deployments: Deployment[];
  history: DeploymentHistory[];
  settings: DeploymentSettings;
}

export interface TestSuite {
  id: string;
  name: string;
  description: string;
  status: 'passed' | 'running' | 'failed' | 'idle';
  lastRun: string;
  duration: string;
  tests: {
    total: number;
    passed: number;
    failed: number;
    skipped: number;
  };
  runner: 'jest' | 'playwright' | 'cypress' | 'vitest';
  command: string;
}

export interface TestEnvironment {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  url: string;
  lastTest: string;
  healthCheck: boolean;
}

export interface TestSettings {
  autoRunOnCommit: boolean;
  parallelExecution: boolean;
  failFast: boolean;
  bypassEnvironmentValidation: boolean;
  generateReports: boolean;
  slackNotifications: boolean;
  retryFailedTests: number;
}

export interface QualityMetrics {
  testCoverage: number;
  codeQuality: string;
  avgTestTime: string;
}

export interface TestResult {
  id: string;
  suiteId: string;
  status: 'passed' | 'failed' | 'running';
  startTime: string;
  endTime?: string;
  duration?: string;
  output: string[];
  errors: string[];
  tests: {
    total: number;
    passed: number;
    failed: number;
    skipped: number;
  };
}

export interface QATestingData {
  testSuites: TestSuite[];
  testEnvironments: TestEnvironment[];
  testSettings: TestSettings;
  qualityMetrics: QualityMetrics;
}

export interface HealthCheckResult {
  environmentId: string;
  url: string;
  status: 'healthy' | 'unhealthy';
  responseTime: number;
  timestamp: string;
  checks: {
    connectivity: boolean;
    database: boolean;
    api: boolean;
    authentication: boolean;
  };
}

export interface DeploymentTrigger {
  deploymentId: string;
  branch?: string;
  environment?: string;
}

class SettingsService {
  private config: SettingsServiceConfig;

  constructor(config: SettingsServiceConfig = {}) {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || 'http://localhost:8000/api',
      timeout: config.timeout || 5000,
    };
  }

  async saveSecureSettings(settings: Partial<SecureSettings>): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(settings),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to save secure settings: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Secure settings saved successfully:', result);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to save secure settings');
        }
        console.error('Error saving secure settings:', error.message);
        throw new Error(`Failed to save secure settings: ${error.message}`);
      }
      throw error;
    }
  }

  async loadSecureSettings(): Promise<Partial<SecureSettings>> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secure`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          console.log('No secure settings found, returning defaults');
          return {};
        }
        throw new Error(`Failed to load secure settings: ${response.status} ${response.statusText}`);
      }

      const settings = await response.json();
      console.log('Secure settings loaded successfully');
      return settings;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.warn('Request timeout: Failed to load secure settings, using defaults');
          return {};
        }
        console.error('Error loading secure settings:', error.message);
      }
      return {};
    }
  }

  async deleteSecureSettings(): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secure`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete secure settings: ${response.status} ${response.statusText}`);
      }

      console.log('Secure settings deleted successfully');
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to delete secure settings');
        }
        console.error('Error deleting secure settings:', error.message);
        throw new Error(`Failed to delete secure settings: ${error.message}`);
      }
      throw error;
    }
  }

  saveLocalSettings(settings: Partial<LocalSettings>): void {
    try {
      const existingSettings = this.loadLocalSettings();
      const updatedSettings = { ...existingSettings, ...settings };
      
      localStorage.setItem('beehive-local-settings', JSON.stringify(updatedSettings));
      console.log('Local settings saved successfully');
    } catch (error) {
      console.error('Error saving local settings:', error);
      throw new Error('Failed to save local settings to localStorage');
    }
  }

  loadLocalSettings(): Partial<LocalSettings> {
    try {
      const stored = localStorage.getItem('beehive-local-settings');
      if (!stored) {
        return {};
      }
      
      const settings = JSON.parse(stored);
      console.log('Local settings loaded successfully');
      return settings;
    } catch (error) {
      console.error('Error loading local settings:', error);
      localStorage.removeItem('beehive-local-settings');
      return {};
    }
  }

  clearLocalSettings(): void {
    try {
      localStorage.removeItem('beehive-local-settings');
      console.log('Local settings cleared successfully');
    } catch (error) {
      console.error('Error clearing local settings:', error);
    }
  }

  async syncAllSettings(): Promise<{
    secure: Partial<SecureSettings>;
    local: Partial<LocalSettings>;
  }> {
    try {
      const [secureSettings, localSettings] = await Promise.all([
        this.loadSecureSettings(),
        Promise.resolve(this.loadLocalSettings()),
      ]);

      return {
        secure: secureSettings,
        local: localSettings,
      };
    } catch (error) {
      console.error('Error syncing settings:', error);
      throw new Error('Failed to sync settings');
    }
  }

  private getAuthToken(): string {
    const token = localStorage.getItem('beehive-auth-token') || 
                  sessionStorage.getItem('beehive-auth-token') ||
                  import.meta.env.VITE_API_TOKEN;
    
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    return token;
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${this.config.apiBaseUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch {
      return false;
    }
  }

  async loadSecrets(): Promise<SecretData[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secrets`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 404) {
          console.log('No secrets found, returning empty array');
          return [];
        }
        throw new Error(`Failed to load secrets: ${response.status} ${response.statusText}`);
      }

      const encryptedSecrets = await response.json();
      
      const decryptedSecrets: SecretData[] = [];
      for (const item of encryptedSecrets) {
        try {
          if (!encryptionService.validateEncryptedData(item.encryptedData)) {
            console.warn(`Invalid encrypted data for secret ${item.metadata.id}, skipping`);
            continue;
          }
          
          const decryptedSecret = encryptionService.decryptSecret(item.encryptedData, item.metadata);
          decryptedSecrets.push(decryptedSecret);
        } catch (error) {
          console.error(`Failed to decrypt secret ${item.metadata.id}:`, error);
        }
      }

      console.log(`Successfully loaded ${decryptedSecrets.length} secrets`);
      return decryptedSecrets;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.warn('Request timeout: Failed to load secrets, returning empty array');
          return [];
        }
        console.error('Error loading secrets:', error.message);
      }
      return [];
    }
  }

  async saveSecret(secret: SecretCreateRequest): Promise<SecretData> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const { encryptedSecret, metadata } = encryptionService.encryptSecret({
        name: secret.name,
        value: secret.value,
        environment: secret.environment,
        lastUsed: 'Never'
      });

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secrets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          encryptedData: encryptedSecret,
          metadata: metadata
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to save secret: ${response.status} ${response.statusText}`);
      }

      await response.json();
      console.log('Secret saved successfully:', metadata.id);
      
      return {
        ...metadata,
        value: secret.value
      };
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to save secret');
        }
        console.error('Error saving secret:', error.message);
        throw new Error(`Failed to save secret: ${error.message}`);
      }
      throw error;
    }
  }

  async updateSecret(secretId: string, updates: SecretUpdateRequest): Promise<SecretData> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      let encryptedData: EncryptedData | undefined;
      const metadata: Partial<SecretMetadata> = {};

      if (updates.value) {
        const { encryptedSecret } = encryptionService.encryptSecret({
          name: updates.name || '',
          value: updates.value,
          environment: updates.environment || 'development',
          lastUsed: 'Never'
        });
        encryptedData = encryptedSecret;
      }

      if (updates.name) metadata.name = updates.name;
      if (updates.environment) metadata.environment = updates.environment;
      metadata.updatedAt = new Date().toISOString();

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secrets/${secretId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          encryptedData,
          metadata
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to update secret: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Secret updated successfully:', secretId);
      
      return result;
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to update secret');
        }
        console.error('Error updating secret:', error.message);
        throw new Error(`Failed to update secret: ${error.message}`);
      }
      throw error;
    }
  }

  async deleteSecret(secretId: string): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secrets/${secretId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok && response.status !== 404) {
        throw new Error(`Failed to delete secret: ${response.status} ${response.statusText}`);
      }

      console.log('Secret deleted successfully:', secretId);
      
      this.logSecretAccess(secretId, 'DELETE');
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to delete secret');
        }
        console.error('Error deleting secret:', error.message);
        throw new Error(`Failed to delete secret: ${error.message}`);
      }
      throw error;
    }
  }

  async rotateSecretKey(secretId: string, oldKey: string, newKey: string): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/settings/secrets/${secretId}/rotate-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          oldKey,
          newKey
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to rotate secret key: ${response.status} ${response.statusText}`);
      }

      console.log('Secret key rotated successfully:', secretId);
      this.logSecretAccess(secretId, 'KEY_ROTATION');
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout: Failed to rotate secret key');
        }
        console.error('Error rotating secret key:', error.message);
        throw new Error(`Failed to rotate secret key: ${error.message}`);
      }
      throw error;
    }
  }

  private logSecretAccess(secretId: string, action: 'READ' | 'WRITE' | 'DELETE' | 'KEY_ROTATION'): void {
    try {
      const auditLog = {
        secretId,
        action,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        ipAddress: 'client-side-unknown'
      };

      fetch(`${this.config.apiBaseUrl}/audit/secrets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(auditLog),
      }).catch(error => {
        console.warn('Failed to log secret access:', error);
      });
    } catch (error) {
      console.warn('Failed to create audit log:', error);
    }
  }
  async loadProfile(): Promise<UserProfile> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to load profile: ${response.status} ${response.statusText}`);
      }

      const profile = await response.json();
      console.log('Profile loaded successfully');
      return profile;
    } catch (error) {
      console.error('Error loading profile:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load profile');
    }
  }

  async updateProfile(updates: ProfileUpdateRequest): Promise<UserProfile> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(updates),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update profile: ${response.status} ${response.statusText}`);
      }

      const updatedProfile = await response.json();
      console.log('Profile updated successfully');
      return updatedProfile;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }

  async loadSubscription(): Promise<BillingSubscription> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/billing/subscription`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Return mock data instead of throwing error
        console.warn('Failed to load subscription, returning mock data');
        return this.getMockSubscription();
      }

      const subscription = await response.json();
      console.log('Subscription loaded successfully');
      return subscription;
    } catch (error) {
      console.error('Error loading subscription:', error);
      // Return mock data instead of throwing error
      return this.getMockSubscription();
    }
  }

  private getMockSubscription(): BillingSubscription {
    return {
      id: 'mock-subscription',
      status: 'active',
      plan: {
        id: 'pro',
        name: 'Pro Plan',
        price: 29.99,
        interval: 'month',
        features: ['Unlimited API calls', 'Priority support', 'Advanced analytics']
      },
      currentPeriodStart: new Date().toISOString(),
      currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      cancelAtPeriodEnd: false,
      usage: {
        apiCalls: 1250,
        storage: 2.5,
        bandwidth: 15.8
      },
      limits: {
        apiCalls: 10000,
        storage: 100,
        bandwidth: 1000
      }
    };
  }

  async updateSubscription(updates: SubscriptionUpdateRequest): Promise<BillingSubscription> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/billing/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(updates),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update subscription: ${response.status} ${response.statusText}`);
      }

      const updatedSubscription = await response.json();
      console.log('Subscription updated successfully');
      return updatedSubscription;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update subscription');
    }
  }

  async loadPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/billing/payment-methods`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Return mock data instead of throwing error
        console.warn('Failed to load payment methods, returning mock data');
        return this.getMockPaymentMethods();
      }

      const paymentMethods = await response.json();
      console.log('Payment methods loaded successfully');
      return paymentMethods;
    } catch (error) {
      console.error('Error loading payment methods:', error);
      // Return mock data instead of throwing error
      return this.getMockPaymentMethods();
    }
  }

  private getMockPaymentMethods(): PaymentMethod[] {
    return [
      {
        id: 'pm_mock_1',
        type: 'card',
        card: {
          brand: 'visa',
          last4: '4242',
          expMonth: 12,
          expYear: 2025
        },
        isDefault: true
      },
      {
        id: 'pm_mock_2',
        type: 'card',
        card: {
          brand: 'mastercard',
          last4: '5555',
          expMonth: 6,
          expYear: 2026
        },
        isDefault: false
      }
    ];
  }

  async addPaymentMethod(token: string, setAsDefault: boolean = false): Promise<PaymentMethod> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/billing/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ token, setAsDefault }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add payment method: ${response.status} ${response.statusText}`);
      }

      const paymentMethod = await response.json();
      console.log('Payment method added successfully');
      return paymentMethod;
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to add payment method');
    }
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/billing/payment-methods/${paymentMethodId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete payment method: ${response.status} ${response.statusText}`);
      }

      console.log('Payment method deleted successfully');
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete payment method');
    }
  }

  async loadIntegrations(): Promise<Integration[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to load integrations: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Integrations loaded successfully');
      return result.data || [];
    } catch (error) {
      console.error('Error loading integrations:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load integrations');
    }
  }

  async connectIntegration(provider: string): Promise<{ authUrl: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/connect/${provider}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to connect ${provider}: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`${provider} connection initiated successfully`);
      return result;
    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      throw new Error(error instanceof Error ? error.message : `Failed to connect ${provider}`);
    }
  }

  async disconnectIntegration(integrationId: string): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to disconnect integration: ${response.status} ${response.statusText}`);
      }

      console.log('Integration disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to disconnect integration');
    }
  }

  async loadWebhooks(): Promise<Webhook[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/webhooks`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to load webhooks: ${response.status} ${response.statusText}`);
      }

      const webhooks = await response.json();
      console.log('Webhooks loaded successfully');
      return webhooks;
    } catch (error) {
      console.error('Error loading webhooks:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load webhooks');
    }
  }

  async createWebhook(webhook: WebhookCreateRequest): Promise<Webhook> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(webhook),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create webhook: ${response.status} ${response.statusText}`);
      }

      const createdWebhook = await response.json();
      console.log('Webhook created successfully');
      return createdWebhook;
    } catch (error) {
      console.error('Error creating webhook:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create webhook');
    }
  }

  async updateWebhook(webhookId: string, updates: WebhookUpdateRequest): Promise<Webhook> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/webhooks/${webhookId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(updates),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update webhook: ${response.status} ${response.statusText}`);
      }

      const updatedWebhook = await response.json();
      console.log('Webhook updated successfully');
      return updatedWebhook;
    } catch (error) {
      console.error('Error updating webhook:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update webhook');
    }
  }

  async deleteWebhook(webhookId: string): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete webhook: ${response.status} ${response.statusText}`);
      }

      console.log('Webhook deleted successfully');
    } catch (error) {
      console.error('Error deleting webhook:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to delete webhook');
    }
  }

  async testWebhook(webhookId: string): Promise<{ success: boolean; message: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/webhooks/${webhookId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to test webhook: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Webhook tested successfully');
      return result;
    } catch (error) {
      console.error('Error testing webhook:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to test webhook');
    }
  }

  async testIntegration(integrationId: string): Promise<{ success: boolean; connected: boolean; message: string; details?: Record<string, unknown> }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/integrations/${integrationId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to test integration: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`Integration ${integrationId} tested successfully`);
      return result;
    } catch (error) {
      console.error(`Error testing integration ${integrationId}:`, error);
      throw new Error(error instanceof Error ? error.message : 'Failed to test integration');
    }
  }

  async loadReferralData(): Promise<ReferralData> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/referrals`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to load referral data: ${response.status} ${response.statusText}`);
      }

      const referralData = await response.json();
      console.log('Referral data loaded successfully');
      return referralData;
    } catch (error) {
      console.error('Error loading referral data:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to load referral data');
    }
  }

  async sendReferralInvite(invite: ReferralInvite): Promise<{ success: boolean; message: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/referrals/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(invite),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to send referral invite: ${response.status} ${response.statusText}`);
      }

      await response.json();
      console.log('Referral invite sent successfully');
      return { success: true, message: 'Referral invite sent successfully' };
    } catch (error) {
      console.error('Error sending referral invite:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to send referral invite');
    }
  }

  async processReferralPayment(referralId: string, amount: number): Promise<{ success: boolean; paymentIntentId: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/referrals/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ referralId, amount }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to process referral payment: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Referral payment processed successfully');
      return result;
    } catch (error) {
      console.error('Error processing referral payment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to process referral payment');
    }
  }

  async redeemReferralCode(referralCode: string): Promise<{ success: boolean; discount: number }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/referrals/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ referralCode }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to redeem referral code: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Referral code redeemed successfully');
      return result;
    } catch (error) {
      console.error('Error redeeming referral code:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to redeem referral code');
    }
  }

  async activateReferral(referralId: string): Promise<{ success: boolean; earnings: number }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/referrals/activate/${referralId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to activate referral: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Referral activated successfully');
      return result;
    } catch (error) {
      console.error('Error activating referral:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to activate referral');
    }
  }

  async loadDeploymentData(): Promise<DeploymentData> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Return mock data instead of throwing error
        console.warn('Failed to load deployment data, returning mock data');
        return this.getMockDeploymentData();
      }

      const deploymentData = await response.json();
      console.log('Deployment data loaded successfully');
      return deploymentData;
    } catch (error) {
      console.error('Error loading deployment data:', error);
      // Return mock data instead of throwing error
      return this.getMockDeploymentData();
    }
  }

  private getMockDeploymentData(): DeploymentData {
    return {
      deployments: [
        {
          id: 'deploy_1',
          name: 'Production App',
          environment: 'production',
          status: 'active',
          url: 'https://app.hivecore.com',
          branch: 'main',
          lastDeploy: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          version: 'v1.2.3',
          health: 'healthy',
          provider: 'vercel',
          providerId: 'vercel_123'
        },
        {
          id: 'deploy_2',
          name: 'Staging App',
          environment: 'staging',
          status: 'active',
          url: 'https://staging.hivecore.com',
          branch: 'develop',
          lastDeploy: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          version: 'v1.2.4-beta',
          health: 'healthy',
          provider: 'netlify',
          providerId: 'netlify_456'
        }
      ],
      history: [
        {
          id: 'hist_1',
          environment: 'production',
          version: 'v1.2.3',
          status: 'success',
          duration: '2m 15s',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          deploymentId: 'deploy_1'
        },
        {
          id: 'hist_2',
          environment: 'staging',
          version: 'v1.2.4-beta',
          status: 'success',
          duration: '1m 45s',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          deploymentId: 'deploy_2'
        }
      ],
      settings: {
        autoDeployOnMerge: true,
        healthChecks: true,
        slackNotifications: false,
        rollbackOnFailure: true
      }
    };
  }

  async triggerDeployment(trigger: DeploymentTrigger): Promise<{ success: boolean; deploymentId: string; status: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(trigger),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to trigger deployment: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Deployment triggered successfully');
      return result;
    } catch (error) {
      console.error('Error triggering deployment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to trigger deployment');
    }
  }

  async stopDeployment(deploymentId: string): Promise<{ success: boolean; status: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments/${deploymentId}/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to stop deployment: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Deployment stopped successfully');
      return result;
    } catch (error) {
      console.error('Error stopping deployment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to stop deployment');
    }
  }

  async rollbackDeployment(deploymentId: string, version?: string): Promise<{ success: boolean; previousVersion: string; newVersion: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments/${deploymentId}/rollback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({ version }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to rollback deployment: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Deployment rollback initiated successfully');
      return result;
    } catch (error) {
      console.error('Error rolling back deployment:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to rollback deployment');
    }
  }

  async getDeploymentLogs(deploymentId: string): Promise<{ logs: Array<{ timestamp: string; level: string; message: string }> }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments/${deploymentId}/logs`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get deployment logs: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Deployment logs retrieved successfully');
      return result;
    } catch (error) {
      console.error('Error getting deployment logs:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get deployment logs');
    }
  }

  async updateDeploymentSettings(settings: Partial<DeploymentSettings>): Promise<{ success: boolean; settings: DeploymentSettings }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/deployments/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(settings),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update deployment settings: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Deployment settings updated successfully');
      return result;
    } catch (error) {
      console.error('Error updating deployment settings:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update deployment settings');
    }
  }

  async loadQATestingData(): Promise<QATestingData> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Return mock data instead of throwing error
        console.warn('Failed to load QA testing data, returning mock data');
        return this.getMockQATestingData();
      }

      const qaData = await response.json();
      console.log('QA testing data loaded successfully');
      return qaData;
    } catch (error) {
      console.error('Error loading QA testing data:', error);
      // Return mock data instead of throwing error
      return this.getMockQATestingData();
    }
  }

  private getMockQATestingData(): QATestingData {
    return {
      testSuites: [
        {
          id: 'suite_1',
          name: 'Unit Tests',
          description: 'Core unit tests for business logic',
          status: 'passed',
          lastRun: new Date(Date.now() - 10 * 60 * 1000).toLocaleString(),
          duration: '45s',
          tests: {
            total: 156,
            passed: 154,
            failed: 0,
            skipped: 2
          },
          runner: 'jest',
          command: 'npm run test:unit'
        },
        {
          id: 'suite_2',
          name: 'Integration Tests',
          description: 'API and database integration tests',
          status: 'passed',
          lastRun: new Date(Date.now() - 30 * 60 * 1000).toLocaleString(),
          duration: '2m 15s',
          tests: {
            total: 89,
            passed: 87,
            failed: 0,
            skipped: 2
          },
          runner: 'jest',
          command: 'npm run test:integration'
        },
        {
          id: 'suite_3',
          name: 'E2E Tests',
          description: 'End-to-end user journey tests',
          status: 'running',
          lastRun: 'Running now',
          duration: '0s',
          tests: {
            total: 23,
            passed: 12,
            failed: 0,
            skipped: 0
          },
          runner: 'playwright',
          command: 'npm run test:e2e'
        }
      ],
      testEnvironments: [
        {
          id: 'env_1',
          name: 'Development',
          status: 'active',
          url: 'http://localhost:3000',
          lastTest: new Date(Date.now() - 5 * 60 * 1000).toLocaleString(),
          healthCheck: true
        },
        {
          id: 'env_2',
          name: 'Staging',
          status: 'active',
          url: 'https://staging.hivecore.com',
          lastTest: new Date(Date.now() - 15 * 60 * 1000).toLocaleString(),
          healthCheck: true
        },
        {
          id: 'env_3',
          name: 'Production',
          status: 'inactive',
          url: 'https://app.hivecore.com',
          lastTest: new Date(Date.now() - 2 * 60 * 60 * 1000).toLocaleString(),
          healthCheck: false
        }
      ],
      testSettings: {
        autoRunOnCommit: true,
        parallelExecution: true,
        failFast: false,
        bypassEnvironmentValidation: false,
        generateReports: true,
        slackNotifications: true,
        retryFailedTests: 2
      },
      qualityMetrics: {
        testCoverage: 87.5,
        codeQuality: 'A',
        avgTestTime: '1m 23s'
      }
    };
  }

  async runTestSuite(suiteId: string): Promise<{ success: boolean; testResult: TestResult; message: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa/test-suites/${suiteId}/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to run test suite: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Test suite executed successfully');
      return result;
    } catch (error) {
      console.error('Error running test suite:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to run test suite');
    }
  }

  async runAllTestSuites(): Promise<{ success: boolean; results: TestResult[]; message: string }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa/test-suites/run-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to run all test suites: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('All test suites executed successfully');
      return result;
    } catch (error) {
      console.error('Error running all test suites:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to run all test suites');
    }
  }

  async getTestResults(suiteId: string): Promise<{ results: TestResult[] }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa/test-suites/${suiteId}/results`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get test results: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Test results retrieved successfully');
      return result;
    } catch (error) {
      console.error('Error getting test results:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get test results');
    }
  }

  async performHealthCheck(environmentId: string): Promise<HealthCheckResult> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa/environments/${environmentId}/health-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to perform health check: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Health check performed successfully');
      return result;
    } catch (error) {
      console.error('Error performing health check:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to perform health check');
    }
  }

  async updateQATestingSettings(settings: Partial<TestSettings>): Promise<{ success: boolean; settings: TestSettings }> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(`${this.config.apiBaseUrl}/qa/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify(settings),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update QA testing settings: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('QA testing settings updated successfully');
      return result;
    } catch (error) {
      console.error('Error updating QA testing settings:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update QA testing settings');
    }
  }

  async loadPropolisConfig(): Promise<PropolisConfig> {
    try {
      const settings = await this.loadSecureSettings();
      const propolisSettings = (settings as any).propolis;
      
      return {
        host: propolisSettings?.host || 'localhost',
        port: propolisSettings?.port || 50051,
        authToken: propolisSettings?.authToken,
        webhookUrl: propolisSettings?.webhookUrl,
        enabled: propolisSettings?.enabled || false
      };
    } catch (error) {
      console.error('Failed to load Propolis config:', error);
      return {
        host: 'localhost',
        port: 50051,
        enabled: false
      };
    }
  }

  async savePropolisConfig(config: PropolisConfig): Promise<void> {
    try {
      const settings = await this.loadSecureSettings();
      (settings as any).propolis = config;
      await this.saveSecureSettings(settings);
      console.log('Propolis configuration saved successfully');
    } catch (error) {
      console.error('Failed to save Propolis config:', error);
      throw new Error(`Failed to save Propolis configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async testPropolisConnection(config?: PropolisConfig): Promise<boolean> {
    try {
      const propolisConfig = config || await this.loadPropolisConfig();
      
      if (!propolisConfig.enabled) {
        console.log('Propolis is disabled in configuration');
        return false;
      }

      // const { PropolisClient } = await import('../../../src/services/PropolisClient.js');
      // const client = PropolisClient.getInstance();
      
      // const health = await client.getHealthStatus();
      // return health.status === 'healthy';
      return false; // Temporarily disabled due to backend dependency
    } catch (error) {
      console.error('Propolis connection test failed:', error);
      return false;
    }
  }

}

export const settingsService = new SettingsService();

export default settingsService;
